import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type GisMapCanvasSkeletonProps = {
  className?: string;
  label?: string;
};

const layerRows = ["layer-1", "layer-2", "layer-3", "layer-4", "layer-5"];
const railItems = ["tool-1", "tool-2", "tool-3", "tool-4", "tool-5", "tool-6"];

export function GisMapCanvasSkeleton({
  className,
  label,
}: GisMapCanvasSkeletonProps) {
  return (
    <div
      data-testid="gis-map-canvas-skeleton"
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden bg-zinc-100 dark:bg-zinc-900",
        className
      )}
      aria-hidden={label ? undefined : true}
      aria-busy={label ? true : undefined}
      aria-label={label}
      role={label ? "status" : undefined}
    >
      <div className="absolute inset-0 opacity-60 [background-image:linear-gradient(to_right,theme(colors.zinc.200)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.zinc.200)_1px,transparent_1px)] [background-size:3rem_3rem] dark:opacity-35 dark:[background-image:linear-gradient(to_right,theme(colors.zinc.700)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.zinc.700)_1px,transparent_1px)]" />
      <div className="absolute -left-16 top-[58%] h-10 w-[44%] -rotate-12 rounded-full bg-zinc-200 dark:bg-zinc-800" />
      <div className="absolute -right-20 top-[24%] h-8 w-[52%] rotate-[28deg] rounded-full bg-zinc-200 dark:bg-zinc-800" />
      <div className="absolute bottom-[18%] left-[28%] h-6 w-[38%] rotate-6 rounded-full bg-zinc-200 dark:bg-zinc-800" />
      <div className="absolute bottom-3 left-1/2 h-5 w-52 -translate-x-1/2 rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
    </div>
  );
}

export function GisWorkspaceLoadingSkeleton({
  label,
  showLayerPanel = false,
}: {
  label: string;
  showLayerPanel?: boolean;
}) {
  return (
    <output
      data-testid="map-workspace-loading-skeleton"
      className={cn(
        "fixed inset-x-0 bottom-0 top-[calc(4rem+env(safe-area-inset-top))] z-20 grid grid-cols-[2.5rem_minmax(0,1fr)] overflow-hidden bg-zinc-100 dark:bg-zinc-900",
        showLayerPanel &&
          "lg:grid-cols-[2.5rem_minmax(0,1fr)_21.5rem]"
      )}
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
    >
      <aside
        data-testid="gis-loading-tool-rail"
        aria-hidden="true"
        className="flex flex-col items-center gap-3 border-r border-zinc-200 bg-white py-3 dark:border-zinc-800 dark:bg-zinc-950"
      >
        {railItems.map((item) => (
          <Skeleton key={item} className="size-6 rounded-md" />
        ))}
      </aside>

      <div aria-hidden="true" className="relative min-w-0 overflow-hidden">
        <GisMapCanvasSkeleton />
        <Skeleton className="absolute left-1/2 top-3 h-10 w-[min(28rem,64%)] -translate-x-1/2 rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
        <div className="absolute right-3 top-3 flex flex-col gap-1">
          <Skeleton className="size-9 rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
          <Skeleton className="size-9 rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
          <Skeleton className="size-9 rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
        </div>
        <Skeleton className="absolute bottom-3 left-3 h-9 w-24 rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
      </div>

      <aside
        data-testid="gis-loading-layer-panel"
        aria-hidden="true"
        className={cn(
          "hidden border-l border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950",
          showLayerPanel && "lg:block"
        )}
      >
        <div className="flex h-12 items-center border-b border-zinc-200 px-3 dark:border-zinc-800">
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="grid h-10 grid-cols-3 border-b border-zinc-200 p-1 dark:border-zinc-800">
          <Skeleton className="h-8 rounded-md" />
          <div />
          <div />
        </div>
        <div className="space-y-3 p-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-10" />
          </div>
          {layerRows.map((row, index) => (
            <div key={row} className="flex h-10 items-center gap-3">
              <Skeleton className="size-2.5 rounded-full" />
              <Skeleton
                className={cn("h-3", index % 2 === 0 ? "w-36" : "w-44")}
              />
              <Skeleton className="ml-auto size-6 rounded-md" />
            </div>
          ))}
          <Skeleton className="mt-3 h-10 w-full rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" />
        </div>
      </aside>
    </output>
  );
}

export function GisStudioLoadingSkeleton({ label }: { label: string }) {
  return (
    <output
      data-testid="gis-studio-loading-skeleton"
      className="fixed inset-0 z-50 grid min-h-svh grid-cols-[13.5rem_minmax(0,1fr)] overflow-hidden bg-zinc-100 dark:bg-zinc-900"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
    >
      <aside
        aria-hidden="true"
        className="border-r border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-950"
      >
        <div className="mb-5 flex items-center gap-2">
          <Skeleton className="size-7 rounded-md" />
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-14" />
          </div>
        </div>
        <div className="mb-6 grid grid-cols-5 gap-2 border-b border-zinc-200 pb-3 dark:border-zinc-800">
          {layerRows.map((item) => (
            <Skeleton key={item} className="h-8 rounded-md" />
          ))}
        </div>
        <div className="space-y-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-24 w-full rounded-md" />
        </div>
      </aside>
      <div aria-hidden="true" className="relative min-w-0 overflow-hidden">
        <div className="flex h-10 items-center justify-between border-b border-zinc-200 bg-white px-3 dark:border-zinc-800 dark:bg-zinc-950">
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
        <div className="absolute inset-x-0 bottom-0 top-10 grid place-items-center bg-zinc-100 p-8 dark:bg-zinc-900">
          <div className="aspect-[1.414/1] h-auto max-h-full w-[min(80%,64rem)] rounded-sm border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-950" />
        </div>
      </div>
    </output>
  );
}
